import React from 'react';
import { Box, CircularProgress, Tooltip, Typography } from '@mui/material';

/**
 * Hover popup listing how long a mare carried in each of her previous breedings.
 *
 * Presentational only — the page owns the fetch and the per-mare cache, and hands
 * it in as `entry`. `onOpen` fires on hover so the page can fetch lazily.
 *
 *   <MareGestationHistoryTooltip
 *     mareId={record.mare_id}
 *     excludeRecordId={record.id}
 *     entry={mareHistory[record.mare_id]}
 *     overrideNote={...}
 *     onOpen={handleMareHistoryOpen}
 *   >
 *     {dueDateContent}
 *   </MareGestationHistoryTooltip>
 */

// The tooltip surface is near-black (theme.js MuiTooltip override), so secondary
// text has to be a dimmed white rather than the palette's text.secondary.
const MUTED = 'rgba(255, 255, 255, 0.65)';

// One grid for the whole table — header and every row are direct children, so the
// columns line up. Per-row grids would each size their own tracks, letting a long
// middle label shove that row's stallion name out of line with the others.
const GRID_SX = {
  display: 'grid',
  gridTemplateColumns: 'auto minmax(72px, auto) 1fr',
  columnGap: 1.5,
  alignItems: 'baseline',
};

const HEADER_CELL_SX = {
  color: MUTED,
  pb: 0.5,
  borderBottom: '1px solid rgba(255,255,255,0.2)',
};

const CELL_SX = { py: 0.4 };

const titleCase = (value) =>
  value ? value.charAt(0).toUpperCase() + value.slice(1).toLowerCase() : null;

// What to show in place of a day count when the gestation never completed.
const outcomeLabel = (row) => {
  const status = (row.pregnancy_status || '').toLowerCase();
  if (status === 'foaled') return 'No foaling date';
  return titleCase(row.pregnancy_status) || '—';
};

function HistoryBody({ entry, excludeRecordId }) {
  if (!entry || entry.status === 'loading') {
    return (
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, py: 0.5 }}>
        <CircularProgress size={12} sx={{ color: MUTED }} />
        <Typography variant="caption" sx={{ color: MUTED }}>
          Loading…
        </Typography>
      </Box>
    );
  }

  if (entry.status === 'error') {
    return (
      <Typography variant="caption" sx={{ color: MUTED }}>
        Couldn&apos;t load breeding history.
      </Typography>
    );
  }

  const rows = (entry.rows || []).filter((row) => row.id !== excludeRecordId);

  if (rows.length === 0) {
    return (
      <Typography variant="caption" sx={{ color: MUTED }}>
        No previous breedings recorded.
      </Typography>
    );
  }

  return (
    <Box sx={GRID_SX}>
      <Typography variant="caption" sx={HEADER_CELL_SX}>Year</Typography>
      <Typography variant="caption" sx={HEADER_CELL_SX}>Days in foal</Typography>
      <Typography variant="caption" sx={HEADER_CELL_SX}>Stallion</Typography>

      {rows.map((row) => (
        <React.Fragment key={row.id}>
          <Typography variant="caption" sx={CELL_SX}>{row.breeding_season ?? '—'}</Typography>
          {row.days_in_foal != null ? (
            <Typography variant="caption" sx={CELL_SX}>{row.days_in_foal} days</Typography>
          ) : (
            <Typography variant="caption" sx={{ ...CELL_SX, color: MUTED }}>
              {outcomeLabel(row)}
            </Typography>
          )}
          <Typography variant="caption" sx={{ ...CELL_SX, color: MUTED }}>
            {row.stallion_name || '—'}
          </Typography>
        </React.Fragment>
      ))}
    </Box>
  );
}

function MareGestationHistoryTooltip({
  mareId,
  excludeRecordId,
  entry,
  overrideNote,
  onOpen,
  children,
}) {
  // Without a mare there is nothing to look up; still honour the override note.
  if (!mareId) {
    return overrideNote ? (
      <Tooltip title={overrideNote} arrow>
        <span>{children}</span>
      </Tooltip>
    ) : (
      children
    );
  }

  const title = (
    <Box sx={{ py: 0.5 }}>
      {overrideNote && (
        <Typography
          variant="caption"
          sx={{ display: 'block', color: MUTED, mb: 0.75 }}
        >
          {overrideNote}
        </Typography>
      )}
      <Typography
        variant="caption"
        sx={{ display: 'block', fontWeight: 700, mb: 0.5 }}
      >
        Previous breedings
      </Typography>
      <HistoryBody entry={entry} excludeRecordId={excludeRecordId} />
    </Box>
  );

  return (
    <Tooltip
      title={title}
      arrow
      placement="left"
      enterDelay={200}
      enterNextDelay={200}
      // On touch this opens via long-press; MUI's 1.5s default auto-dismiss is
      // not long enough to read a table.
      leaveTouchDelay={6000}
      onOpen={() => onOpen(mareId)}
      slotProps={{ tooltip: { sx: { maxWidth: 360 } } }}
    >
      {/* Tooltip needs a ref-forwarding child, and the cell content may be a string. */}
      <span>{children}</span>
    </Tooltip>
  );
}

export default MareGestationHistoryTooltip;
